// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-02-12 02:09:13 +0100 (Thu, 12 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160637 $
//  Purpose   : setup part registry and load global parts
//              also wiki-templates, jobs, dashpanels and columns
// =============================================================================
// ---Third-party imports
import React from 'react';
import { isValidElementType, isElement } from 'react-is';
// ---Framework imports
import { each, size, keys, items, mapItems, iterItems, dict, merge, isObject } from 'ldash';
import log from 'uix/logging';
log.info('setup registries (parts, templates, jobs, dashpanels, columns)');
export { funcPart, StandalonePart, createParts, createPart, selectPartData } from './part';
import { UnknownPart, funcPart } from './part';
import connect from 'uix/connect';
const coreParts = {
    UnknownPart
};
import { WikiTemplates, JobTemplates, DashboardPanels, NodeMenuItems, Parts } from 'uix/registry';
import { loadLazy } from 'uix/utils';
export { loadLazy };
// ---Globals
let normalizeParts = (parts)=>/* normalize raw react types to funcParts
   */ dict(mapItems(parts, (n, p)=>{
        let isFuncPart = p.__render_f // funcPart() generated
        ;
        if (isFuncPart) {
            return [
                n,
                p
            ];
        } else if (isValidElementType(p)) {
            // auto transform valid react types to simple part funcs
            return [
                n,
                funcPart((props)=>/*#__PURE__*/ React.createElement(p, props))
            ];
        } else if (isElement(p)) {
            // also raw fragments
            return [
                n,
                funcPart(()=>p)
            ];
        } else {
            throw Error(`exported invalid Part "${n}" in uix/${modName}/parts`);
        }
    }));
Parts.register(normalizeParts(coreParts));
import { partModules, templateModules, jobModules, dashboardModules, nodeMenuModules, columnModules, partProperties, nestedModuleNames } from 'uix/registries';
// we read and store part props before we wrap the part via connect() HOC
let readProps = (parts)=>iterItems(parts, (partType, partDesc)=>{
        partProperties[partType] = {
            getGraphics: partDesc.getGraphics || [],
            getChunkedUtils: partDesc.getChunkedUtils || [],
            filters: partDesc.filters || []
        };
    //~ if (partType.includes('State')) log('part %s: %o', partType, partDesc.getGraphics)
    });
readProps(coreParts);
let moduleNames = keys({
    ...partModules,
    ...templateModules,
    ...jobModules,
    ...dashboardModules,
    ...nodeMenuModules
});
const N = (name)=>{
    if (nestedModuleNames[name]) {
        return `modules/${name}`;
    }
    return `${name}`;
};
// setup custom imports, key is importable filename in module
// isActive could also be a function, receiving the filename
const registrations = {
    parts: {
        isActive: partModules,
        register: (parts)=>{
            parts = normalizeParts(parts);
            Parts.register(parts);
            readProps(parts);
        }
    },
    templates: {
        isActive: templateModules,
        register: (templates)=>merge(WikiTemplates, templates)
    },
    jobs: {
        isActive: jobModules,
        register: (jobs)=>merge(JobTemplates, jobs)
    },
    dashpanels: {
        isActive: dashboardModules,
        register: (panels)=>merge(DashboardPanels, panels)
    },
    nodemenu: {
        isActive: nodeMenuModules,
        register: (menutitems)=>merge(NodeMenuItems, menutitems)
    },
    columns: {
        isActive: columnModules,
        register: (columns)=>registerColumnsSafe(columns)
    }
};
export const loadLazyPart = (partName, importFuncOrPromise, descr = {})=>loadLazy(async ()=>{
        let mod = importFuncOrPromise instanceof Promise ? await importFuncOrPromise : await importFuncOrPromise();
        let comp = mod?.default ?? mod;
        if (isObject(comp) && comp.__render_f) {
            let { __render_f, ...partDescr } = comp;
            return {
                default: connect(`Part/${partName}`, {
                    ...partDescr,
                    ...descr
                }, __render_f)
            };
        }
        return {
            default: comp
        };
    });
const promises = [];
const loadedPromises = [];
let registerColumns = null;
const pendingColumnRegistrations = [];
// Route all column registrations through a single gate:
// - immediate when db/columns is ready
// - queued while the lazy chunk is still loading
const registerColumnsSafe = (cols)=>{
    if (registerColumns) {
        registerColumns(cols);
        return;
    }
    pendingColumnRegistrations.push(cols);
};
// Replay buffered registrations once registerColumns becomes available.
const flushPendingColumnRegistrations = ()=>{
    if (!registerColumns || size(pendingColumnRegistrations) === 0) {
        return;
    }
    pendingColumnRegistrations.forEach((cols)=>registerColumns(cols));
    pendingColumnRegistrations.length = 0;
};
// #if process.env.UIX_PROD_CHUNKED !== 'true'
// registerColumns = require('uix/db/columns').registerColumns
// #endif
// #if process.env.UIX_PROD_CHUNKED === 'true'
// Keep db/columns out of the entry chunk and join the existing 'core' lazy-once group.
// We also participate in the shared loader readiness tracking via promises/loadedPromises.
let dbColumnsPromise = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ 'uix/db/columns').then((module1)=>{
    registerColumns = module1.registerColumns;
    flushPendingColumnRegistrations();
    loadedPromises.push(true);
}).catch((error)=>{
    log.exception('failed to load db/columns', error);
    loadedPromises.push(true);
});
promises.push(dbColumnsPromise);
// #endif
// XXX: also see https://github.com/webpack/webpack/issues/5029
// for prod/dev hot-reload aspect
// 2023-01-23: hot-reload is no longer a problem after switching to fast-refresh
// but require() is still needed for SSR build
const loadModule = (mod, filename, cfg)=>{
    let objects = null;
    let p = null;
    let getObjects = (module1)=>module1 // assume module exports a dictionary, register will probably merge
    ;
    // we need to spell out the imports here
    if (filename == 'parts') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/parts`)
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/parts`);
    // #endif
    } else if (filename == 'templates') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/templates`)
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/templates`);
    // #endif
    } else if (filename == 'jobs') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/jobs`)
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/jobs`);
    // #endif
    } else if (filename == 'dashpanels') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/dashpanels`)
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/dashpanels`);
    // #endif
    } else if (filename == 'nodemenu') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/nodemenu`)
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/nodemenu`);
    // #endif
    } else if (filename == 'columns') {
        // #if process.env.UIX_PROD_CHUNKED !== 'true'
        //     objects = require(`uix/${N(mod)}/columns`).columns
        // #endif
        // #if process.env.UIX_PROD_CHUNKED === 'true'
        p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/columns`);
        getObjects = (module1)=>module1.columns;
    // #endif
    }
    if (objects) {
        cfg.register(objects);
    } else if (p) {
        p.then((module1)=>{
            //~ log.x(`imported ${modName}/${filename}`)
            cfg.register(getObjects(module1));
            loadedPromises.push(true);
        });
        promises.push(p);
    }
    return [
        `%o ${filename}`,
        size(objects)
    ];
};
const loadModules = ()=>{
    log.info('loading modules');
    moduleNames.forEach((modName1)=>{
        try {
            let msgs = [];
            for (let [filename, cfg] of items(registrations)){
                if (cfg.isActive[modName1]) {
                    let msg = loadModule(modName1, filename, cfg);
                    msgs = [
                        ...msgs,
                        msg
                    ];
                }
            }
            let msg = msgs.filter((e)=>e).map((e)=>e[0]).join(', ');
            let values = msgs.filter((e)=>e).map((e)=>e[1]);
            log.startup(`%o: register ${msg}`, modName1, ...values);
        } catch (e) {
            log.exception('error during module load', e);
        }
    });
};
// #if process.env.UIX_PROD_CHUNKED !== 'true'
// // load in core chunk
// loadModules()
// #endif
// #if process.env.UIX_PROD_CHUNKED === 'true'
import { registerLoader } from 'uix/loadmodules';
const isReady = ()=>{
    return size(loadedPromises) === size(promises);
};
if (module.hot) {
    loadModules();
    if (process.env.NODE_ENV === 'development' && process.env.RSPACK === 'true') {
        // Rspack doesn't automatically wire HMR accept handlers for our dynamic
        // "parts" registry modules. In webpack, editing a parts file triggers a
        // hot update that is accepted by the importer, but in Rspack the update can
        // be reported while the registry module itself stays stale. We explicitly
        // accept each parts module and re-run loadModules() so Rspack applies the
        // updated module exports without forcing a full page reload. This is scoped
        // to dev + Rspack only to avoid altering webpack behavior.
        keys(partModules).forEach((name)=>{
            const path = `uix/${N(name)}/parts`;
            module.hot.accept(path, ()=>{
                try {
                    loadModules();
                } catch (e) {
                    console.error('parts registry: refresh failed', e);
                }
            });
        });
    }
} else {
    registerLoader(loadModules, isReady);
} // #endif
